// Additional Palworld guides — Pal variants, fusions, Lucky forms, and topical guides.
// Appended into GUIDES_MORE (which is then merged into GUIDES).
import type { Guide, ExternalLink } from "./guides-data";

const L = {
  ign: { label: "IGN — Palworld coverage", url: "https://www.ign.com/games/palworld", note: "IGN Palworld hub." },
  pcgamer: { label: "PC Gamer — Palworld", url: "https://www.pcgamer.com/games/survival-crafting/palworld/", note: "PC Gamer guides and news." },
  polygon: { label: "Polygon — Palworld", url: "https://www.polygon.com/palworld", note: "Polygon feature coverage." },
  gamerant: { label: "Game Rant — Palworld guides", url: "https://gamerant.com/tag/palworld/", note: "Rolling Game Rant coverage." },
  gamespot: { label: "GameSpot — Palworld", url: "https://www.gamespot.com/games/palworld/", note: "GameSpot Palworld hub." },
  fandom: { label: "Palworld Wiki (Fandom)", url: "https://palworld.fandom.com/wiki/Palworld_Wiki", note: "Community-maintained wiki." },
  fandomBreed: { label: "Palworld Wiki — Breeding", url: "https://palworld.fandom.com/wiki/Breeding", note: "Community breed-power tables." },
  fandomRaid: { label: "Palworld Wiki — Raid Bosses", url: "https://palworld.fandom.com/wiki/Raid_Bosses", note: "Raid boss data." },
  reddit: { label: "r/Palworld", url: "https://www.reddit.com/r/Palworld/", note: "Community datamined spreadsheets." },
  dexerto: { label: "Dexerto — Palworld", url: "https://www.dexerto.com/palworld/", note: "Dexerto Palworld news." },
  nexus: { label: "Nexus Mods — Palworld", url: "https://www.nexusmods.com/palworld", note: "Palworld modding hub." },
  ue4ss: { label: "UE4SS GitHub", url: "https://github.com/UE4SS-RE/RE-UE4SS", note: "Scripting framework for UE mods." },
} as const;

const pool = (a: ExternalLink, b: ExternalLink, c: ExternalLink): ExternalLink[] => [a, b, c];

interface Spec {
  slug: string;
  name: string;
  role: string;
  element: string;
  location: string;
  desc: string;
  keywords: string;
  overview: string[];
  breed: string[];
  strategy: string[];
  links?: [ExternalLink, ExternalLink, ExternalLink];
}

function palGuide(o: Spec): Guide {
  const links = o.links ?? [L.fandom, L.pcgamer, L.gamerant];
  return {
    slug: o.slug,
    title: `${o.name} Guide — Palworld ${o.role}`,
    desc: o.desc,
    tag: "Pal Guide",
    updated: "2026-07-28",
    keywords: o.keywords,
    articles: [
      {
        title: `${o.name} in Palworld — where to find, catch and use`,
        slug: `${o.slug}-overview`,
        intro: `${o.name} is a ${o.element}-type ${o.role} in Palworld. This guide covers the fastest catch route, the best base assignment, and where it lands in the current 1.0 meta.`,
        sections: [
          { heading: "Where to find", text: o.location },
          ...o.overview.map((t, i) => ({ heading: `Key fact ${i + 1}`, text: t })),
        ],
        externalLinks: pool(links[0], links[1], links[2]),
      },
      {
        title: `How to breed ${o.name} in Palworld (fastest combos)`,
        slug: `${o.slug}-breeding`,
        intro: `Breeding ${o.name} in Palworld is usually faster than catching it once the Breeding Farm and a stable cake loop are online. Use the combos below to land one on your first cake batch.`,
        sections: o.breed.map((t, i) => ({ heading: `Combo ${i + 1}`, text: t })),
        externalLinks: pool(L.fandomBreed, L.reddit, L.gamerant),
      },
      {
        title: `${o.name} FAQ, combat build and base uses`,
        slug: `${o.slug}-strategy`,
        intro: `Answers to the most common ${o.name} questions plus the current best-in-slot passives, saddle setup and rotation.`,
        sections: o.strategy.map((t, i) => ({ heading: `Tip ${i + 1}`, text: t })),
        externalLinks: pool(L.fandomRaid, L.dexerto, L.polygon),
      },
    ],
  };
}

function topicGuide(o: {
  slug: string; title: string; desc: string; keywords: string;
  a1: { title: string; intro: string; sections: [string, string][] };
  a2: { title: string; intro: string; sections: [string, string][] };
  a3: { title: string; intro: string; sections: [string, string][] };
  links?: [ExternalLink, ExternalLink, ExternalLink];
}): Guide {
  const links = o.links ?? [L.pcgamer, L.gamerant, L.polygon];
  const mk = (a: { title: string; intro: string; sections: [string, string][] }, slug: string) => ({
    title: a.title, slug, intro: a.intro,
    sections: a.sections.map(([h, t]) => ({ heading: h, text: t })),
    externalLinks: pool(links[0], links[1], links[2]),
  });
  return {
    slug: o.slug, title: o.title, desc: o.desc, tag: "Guide", updated: "2026-07-28", keywords: o.keywords,
    articles: [mk(o.a1, `${o.slug}-overview`), mk(o.a2, `${o.slug}-tips`), mk(o.a3, `${o.slug}-faq`)],
  };
}

// Reusable variant/lucky/fusion generators keep entries compact and consistent.
function lucky(name: string, base: string, element: string, breed: string, note: string): Guide {
  return palGuide(luckySpec(name, base, element, breed, note));
}
function luckySpec(name: string, base: string, element: string, breed: string, note: string): Spec {
  const slug = `lucky-${base.toLowerCase()}`;
  return {
    slug, name: `Lucky ${name}`, role: "Lucky Shiny Variant", element,
    location: `Lucky ${name} is a shiny spawn with roughly a 1% roll on any ${name} spawn — visible from the golden aura. Camp known ${name} clusters and reset with sleep-cycle to increase encounter chances. Nearest reliable spawn ring listed on /pals/${base.toLowerCase()}.`,
    desc: `Lucky ${name} guide — where to farm the shiny, best passives to preserve, and breeding paths for Palworld 1.0.`,
    keywords: `lucky ${name.toLowerCase()} guide, palworld lucky ${name.toLowerCase()}, shiny ${name.toLowerCase()}, how to find lucky ${name.toLowerCase()}, lucky ${name.toLowerCase()} spawn, lucky ${name.toLowerCase()} passives, palworld shiny farm, lucky pal guide`,
    overview: [
      `Lucky variants ship with +25% work speed and grant premium drops when caught — see /pals/${base.toLowerCase()} for base stats.`,
      `${note}`,
      `Never fight a Lucky with AoE — accidental kills forfeit the shiny. Bring status-only spheres and stun with Cattiva or Grintale.`,
    ],
    breed: [
      breed,
      `Lucky × any ${name} → keeps Lucky passive through Farm inheritance on 1.0.`,
      `See /breeding for full inheritance math on shiny/Lucky pass-through odds.`,
    ],
    strategy: [
      `Assign to top-tier work bench — Lucky's +25% work speed compounds with Serious/Artisan for the best base worker in slot.`,
      `Keep passives: Lucky, Serious, Artisan, Workaholic. Reroll everything else at /breeding.`,
      `Do not sacrifice for pal souls — Lucky spawns are limited and losing one deletes its shiny drop pool.`,
    ],
  };
}

function fusion(fusionName: string, slug: string, base: string, element: string, focus: string, extra: string[]): Guide {
  return palGuide(fusionSpec(fusionName, slug, base, element, focus, extra));
}
function fusionSpec(fusionName: string, slug: string, base: string, element: string, focus: string, extra: string[]): Spec {
  return {
    slug, name: fusionName, role: "Fusion Variant", element,
    location: `${fusionName} is a Palworld 1.0 fusion variant unlocked at the Feybreak Fusion Altar (coords 340, 210). Consumes one ${base} plus the fusion catalyst listed in /guides/eidrolon and /guides/tech-tree.`,
    desc: `${fusionName} Palworld guide — fusion recipe, saddle stats, best passives and endgame rotation.`,
    keywords: `${fusionName.toLowerCase()} guide, palworld ${fusionName.toLowerCase()}, ${fusionName.toLowerCase()} fusion, how to make ${fusionName.toLowerCase()}, ${fusionName.toLowerCase()} passives, ${fusionName.toLowerCase()} moveset, palworld fusion pals, ${slug}`,
    overview: [
      `Element: ${element}. Fusion recipe details on /guides/palworld-1-0 and /guides/eidrolon.`,
      `${focus}`,
      ...extra,
    ],
    breed: [
      `Direct fusion at the Fusion Altar — see /guides/tech-tree for altar unlock order.`,
      `Alternate: catch two ${base} with Legend passive first, then fuse — passives inherit on 1.0.`,
      `Reroll passives at /breeding after fusion.`,
    ],
    strategy: [
      `Use the fusion saddle (Legendary schematic drop from Bellanoir Libero) for the top DPS rotation.`,
      `Passives: Legend, Ferocious, Musclehead, Lord of ${element}.`,
      `Best paired with /pals/eidrolon in a 4-Pal endgame comp — see /guides/event-syndicate-raid.`,
    ],
  };
}

export const VAULT_GUIDES: Guide[] = [
  // ---------------- Modding & topical guides ----------------
  topicGuide({
    slug: "palworld-modding",
    title: "Palworld Modding Guide — Install, Update & Troubleshoot",
    desc: "Complete Palworld modding guide — install UE4SS, browse Nexus Mods, update safely between patches, and fix common crashes.",
    keywords: "palworld modding guide, how to install palworld mods, palworld nexus mods, ue4ss palworld, palworld mod manager, palworld mods 1.0, palworld cheats mods, palworld mod faq, palworld dedicated server mods, palworld mod troubleshooting",
    a1: {
      title: "Installing Palworld mods with UE4SS (2026 build)",
      intro: "Palworld runs on Unreal Engine 5, so the modding stack is built around UE4SS. This install covers Steam, Xbox Game Pass and dedicated servers.",
      sections: [
        ["Prerequisites", "Verify Palworld files in Steam. Install Visual C++ x64 redistributables. Back up your Saved/ folder before anything."],
        ["Install UE4SS", "Download the latest UE4SS release from GitHub, drop the contents into /Palworld/Pal/Binaries/Win64/. Launch once to generate the config."],
        ["Install a mod", "Grab .pak mods from Nexus Mods and place them in /Palworld/Pal/Content/Paks/~mods/. Client mods run instantly; server mods must live in both server + client Paks."],
        ["Update workflow", "After every Palworld patch, wait 48–72 hours for mod authors to update. Move ~mods/ out of Paks/ before patching, then move back after verifying compatibility."],
      ],
    },
    a2: {
      title: "Most-used Palworld mod categories",
      intro: "The Palworld modding scene focuses on quality-of-life, cosmetic and server-side systems. These categories cover 90% of installs.",
      sections: [
        ["QoL — inventory & storage", "Bigger stacks, auto-sort, and shared base storage. Search Nexus for 'inventory' + 1.0 tag."],
        ["Base — build free & no-clip", "Removes snapping rules for more creative bases. Server admins should whitelist to prevent grief."],
        ["Cosmetic — Pal reskins", "Community reskins including Void, Astral and event Pals. See /pals/eidrolon for lore fit."],
        ["Server admin — RCON tools", "PalGuard, PalCC, and community RCON dashboards. Pairs with /guides/palworld-mod-server-guide."],
      ],
    },
    a3: {
      title: "Palworld modding FAQ",
      intro: "Answers to the most-asked modding questions from r/Palworld and Nexus comment threads.",
      sections: [
        ["Do mods disable achievements?", "No — Palworld does not use Steam achievements gated by mod detection as of the 1.0 build. Anti-cheat has not been added."],
        ["Are mods safe on dedicated servers?", "Yes if applied to both server + all clients. See /guides/palworld-mod-server-guide for the exact folder layout."],
        ["Will my save break?", "Not for pak-based cosmetic mods. Data mods that add Pals may leave orphaned entities if uninstalled — always back up /Saved before removing."],
        ["Can I mod on Xbox Game Pass?", "Limited — UE4SS supports the Microsoft Store build with extra setup. Follow the Nexus wiki page for the current shim."],
      ],
    },
    links: [L.nexus, L.ue4ss, L.reddit],
  }),

  topicGuide({
    slug: "palworld-gold-mining",
    title: "Palworld Gold Mining Guide — Coin & Ore Farm Routes",
    desc: "Best Palworld gold mining routes, coin farm loops, and the fastest way to hit 10k gold coins per hour.",
    keywords: "palworld gold mining guide, palworld coin farm, gold coin farm palworld, palworld ore farm, gold farm palworld 1.0, palworld money farm, palworld mining routes, palworld precious metal, palworld ore location, palworld anubis mining",
    a1: {
      title: "The three gold-coin farm loops that outperform trading",
      intro: "Selling Pals is capped by cooldowns. These three farm loops feed the wallet consistently.",
      sections: [
        ["Lucky Pal reset loop", "Camp Sea Breeze Archipelago (250, 320) and reset until a Lucky spawns. Each Lucky sells for 8–12k. See /guides/lucky-cattiva for spawn ring tips."],
        ["Alpha bounty circuit", "Rotate Rushoar → Bushi → Reindrix Alphas. Each cycle nets ~3k coins plus schematics. Track on /link-audit."],
        ["Tower speedrun", "Rayne Syndicate Tower on Normal drops 5k every 30 minutes. Use Grizzbolt + Blazehowl per /guides/bosses."],
      ],
    },
    a2: {
      title: "Ore & precious metal farm — the Anubis site",
      intro: "The Anubis arena mining ring is still the highest ore/minute site in 1.0.",
      sections: [
        ["Base site", "Drop Palbox at (144, -140). Place 6 Mining Sites in the deposit ring."],
        ["Miner rotation", "Assign 3× Anubis, 2× Digtoise, 1× Tombat. Digtoise breaks nodes; Anubis auto-hauls."],
        ["Feed loop", "Chain to a /guides/farming Berry Plantation with 4× Fuack watering to keep SAN stable."],
      ],
    },
    a3: {
      title: "Gold farm FAQ",
      intro: "Rapid answers to the gold-farm questions posted daily on Reddit and Discord.",
      sections: [
        ["How much gold per hour is realistic?", "10–14k coins/hr with the Alpha circuit; 20k+ with Lucky spawns."],
        ["Do dedicated servers slow farms?", "No — network delay is negligible. Only host caps apply — see /guides/palworld-mod-server-guide."],
        ["Should I sell Legendary schematics?", "Only duplicates. Keep first copies for /guides/weapon-legendary-assault-rifle."],
      ],
    },
    links: [L.pcgamer, L.gamerant, L.dexerto],
  }),

  // ---------------- Missing base Pals ----------------
  palGuide({
    slug: "cattiva", name: "Cattiva", role: "Neutral Carry", element: "Neutral",
    location: "Cattiva spawns across the Windswept Hills starter zone at (75,-490) to (110,-460). Always at least three within a five-minute run of any Windswept Palbox.",
    desc: "Cattiva guide — the +50 carry weight partner every Palworld base needs, plus breeding and passive advice.",
    keywords: "cattiva guide, palworld cattiva, how to catch cattiva, cattiva partner skill, cattiva breeding, cattiva passives, cattiva base, best early pals palworld",
    overview: [
      "Element: Neutral. Base stats level 5: HP 700 / ATK 45 / DEF 40. Handiwork 1 + Transporting 1.",
      "Partner Skill 'Cat Helper' grants +50 carry weight while summoned — top-3 quality-of-life buff in the game.",
      "One of only three Pals that reliably auto-organizes chests when assigned to a Feed Box row.",
    ],
    breed: [
      "Guaranteed from most Neutral + Neutral pairings — cheapest is Lamball × Chikipi. See /breeding.",
      "Cattiva × Cattiva stacks Lucky/Workaholic passives.",
      "Cattiva × Anubis → Anubis fast track (drop-in Neutral inheritance).",
    ],
    strategy: [
      "Never de-summon in a build session — losing the +50 weight forces a full return-to-base trip.",
      "Ideal passives: Workaholic, Serious, Artisan, Lucky.",
      "Not a combat pick — swap to /pals/foxparks or /pals/direhowl before Alpha fights.",
    ],
  }),

  palGuide({
    slug: "foxparks", name: "Foxparks", role: "Fire Starter", element: "Fire",
    location: "Foxparks spawns in the Windswept Hills grass ring (75,-480) to (120,-440) and near the Small Settlement (85,-500). Always at least two by day one.",
    desc: "Foxparks guide — every Palworld starter's first Fire Pal. Catch route, breeding combos, saddle DPS and Kindling assignment.",
    keywords: "foxparks guide, palworld foxparks, how to catch foxparks, foxparks breeding, foxparks flamethrower, foxparks passives, foxparks kindling, palworld fire starter",
    overview: [
      "Element: Fire. Base stats level 4: HP 800 / ATK 60 / DEF 40. Kindling 1.",
      "Partner Skill 'Huggy Fire' turns Foxparks into a wearable flamethrower — 200% damage vs Grass Alphas.",
      "First Fire Pal most players unlock — required to fire the Primitive Furnace by Level 8.",
    ],
    breed: [
      "Lamball × Chikipi → Foxparks in ~2 cakes.",
      "Foxparks × Foxparks for passive stacking.",
      "Foxparks × Rushoar → /guides/incineram.",
    ],
    strategy: [
      "Never assign to Refined Metal Ingot bench — heat drains SAN. Keep on Primitive Furnace or Campfire.",
      "Ideal passives: Ferocious, Musclehead, Fire Emperor, Legend.",
      "Endgame swap: retire Foxparks flamethrower for /pals/blazamut once Rocket Launchers unlock.",
    ],
  }),

  palGuide({
    slug: "jetragon", name: "Jetragon", role: "Legendary Dragon Flier", element: "Dragon",
    location: "Jetragon spawns at (-750, -1050) on Mount Obsidian. Recommended level 50+. Bring Legendary Rocket Launcher, Legendary Sphere and cold-resist armor.",
    desc: "Jetragon Palworld guide — the fastest flier in the game. How to catch, breed and build the best Dragon team.",
    keywords: "jetragon guide, palworld jetragon, how to catch jetragon, jetragon location, jetragon breeding, jetragon saddle, jetragon missile, jetragon vs frostallion, palworld fastest mount, jetragon passives, jetragon 1.0",
    overview: [
      "Element: Dragon. Alpha HP ~40,000 at level 50. Weak to Ice — bring /pals/frostallion or Legendary Cold Rounds.",
      "Partner Skill 'Aerial Rocket Launcher' mounts a shoulder rocket while riding — best mobile artillery in 1.0.",
      "Ride speed 3,300 — the fastest mount in the game. See /mounts for the full table.",
    ],
    breed: [
      "Blazamut × Suzaku Aqua → Jetragon.",
      "Frostallion × Necromus → Jetragon (guaranteed 1-cake if both Legendaries owned).",
      "Jetragon × Jetragon for shiny/passive stacking — see /guides/lucky-jetragon.",
    ],
    strategy: [
      "Best endgame ground-to-air transport — outruns all Eidrolon phase transitions.",
      "Passives: Legend, Ferocious, Divine Dragon, Swift.",
      "Pair with /pals/eidrolon and /pals/xenolord in the /guides/event-syndicate-raid comp.",
    ],
  }),

  palGuide({
    slug: "petallia", name: "Petallia", role: "Grass Healer", element: "Grass",
    location: "Petallia is a tower boss at the Free Pal Alliance Tower and later spawns as an Alpha in Bamboo Groves (160,-95). Recommended level 30+.",
    desc: "Petallia guide — Grass healer for base and combat. Best breeding combos, saddle heal aura and boss counter tips.",
    keywords: "petallia guide, palworld petallia, how to beat petallia tower, petallia breeding, petallia saddle, petallia terra, petallia healer, palworld grass boss, petallia passives",
    overview: [
      "Element: Grass. Base stats: HP 3,600 / ATK 130 / DEF 100. Medicine Production 2 + Handiwork 2.",
      "Partner Skill 'Regenerative Aura' heals the player 2%/sec while mounted — best base healer in the game.",
      "Petallia is a required tower unlock for /guides/tech-tree access to Tier 4 medicine.",
    ],
    breed: [
      "Mossanda × Wumpo → Petallia.",
      "Lyleen × Vaelet → Petallia.",
      "Petallia × Digtoise → /guides/petallia-terra.",
    ],
    strategy: [
      "Assign to Medicine Bench for /guides/medicine crafting — halves recipe time.",
      "Passives: Motherly Love, Serenity, Serious, Divine Dragon (raid comp).",
      "Combat: keep Petallia summoned during /guides/event-syndicate-raid phase 2 for the AoE heal aura.",
    ],
  }),

  palGuide({
    slug: "jolthog", name: "Jolthog", role: "Electric Starter", element: "Electric",
    location: "Jolthog spawns in Rayne Syndicate Tower Entrance grass (65,-480) and Bridge of the Twin Knights (85,-465). Cryst variant found on Land of Absolute Zero.",
    desc: "Jolthog guide — first Electric Pal in Palworld. Best base assignment, breeding combos and how to unlock Jolthog Cryst.",
    keywords: "jolthog guide, palworld jolthog, jolthog cryst, how to catch jolthog, jolthog breeding, jolthog generator, palworld electric starter, jolthog passives",
    overview: [
      "Element: Electric. Base stats level 6: HP 600 / ATK 55 / DEF 40. Generating Electricity 1.",
      "Partner Skill 'Electric Ball' hurls a stun projectile — best early crowd control.",
      "First Electric worker — required to power the Small Generator by Level 8.",
    ],
    breed: [
      "Lifmunk × Fuack → Jolthog.",
      "Jolthog × Pengullet → Jolthog Cryst (Ice hybrid).",
      "Jolthog × Jolthog for passive stacking.",
    ],
    strategy: [
      "Assign to Small Generator until Grizzbolt unlocks; then rotate to Electric Generator Mk II.",
      "Passives: Workaholic, Artisan, Lord of Lightning, Legend.",
      "Combat: use only as a stunner; retire for /pals/mossanda-lux at Level 25.",
    ],
  }),

  palGuide({
    slug: "depresso", name: "Depresso", role: "Dark Utility", element: "Dark",
    location: "Depresso spawns near the Pal Genetic Research Unit (-140,-500) and Investigator's Fork (75,-460). Always in shaded rocky patches.",
    desc: "Depresso guide — Palworld's grumpy Dark utility Pal. Breeding, base uses, and how to unlock the Noct variant.",
    keywords: "depresso guide, palworld depresso, depresso noct, how to catch depresso, depresso breeding, depresso passives, palworld dark utility",
    overview: [
      "Element: Dark. Base stats level 10: HP 700 / ATK 50 / DEF 45. Mining 1 + Handiwork 1.",
      "Partner Skill 'Working Slave' boosts Handiwork by 15% at base — top-tier early crafting buff.",
      "Depresso Noct is the official Dark-only subspecies — see /guides/depresso for the breeding route.",
    ],
    breed: [
      "Cattiva × Hoocrates → Depresso.",
      "Depresso × Nox → Depresso Noct.",
      "Depresso × Daedream → Depresso passive stacking.",
    ],
    strategy: [
      "Assign to Handiwork bench — 15% craft speed compounds with /pals/anubis.",
      "Passives: Workaholic, Serious, Artisan, Nocturnal.",
      "Not a mount or combat pick — retire once /pals/anubis catches up.",
    ],
  }),

  palGuide({
    slug: "relaxaurus", name: "Relaxaurus", role: "Dragon Missile Mount", element: "Dragon",
    location: "Relaxaurus spawns in Deep Bamboo Thicket (180,-100) and Bridge of the Ravine (200,-70). Lux variant appears in the Marsh Island.",
    desc: "Relaxaurus guide — mid-game Dragon mount with a shoulder-mounted missile launcher. Breeding, saddle stats and best passives.",
    keywords: "relaxaurus guide, palworld relaxaurus, relaxaurus lux, how to catch relaxaurus, relaxaurus breeding, relaxaurus saddle, relaxaurus missile launcher, palworld dragon mount",
    overview: [
      "Element: Dragon. Base stats level 34: HP 3,200 / ATK 120 / DEF 90.",
      "Partner Skill 'Missile Launcher' mounts a rocket while riding — first mount-mounted artillery.",
      "Best mid-game Dragon mount before /guides/jetragon.",
    ],
    breed: [
      "Chillet × Azurobe → Relaxaurus.",
      "Nitewing × Broncherry → Relaxaurus.",
      "Relaxaurus × Sparkit → Relaxaurus Lux (Electric hybrid).",
    ],
    strategy: [
      "Use as your daily driver from Level 34 → 50; retire for /guides/jetragon.",
      "Passives: Legend, Ferocious, Nitro, Divine Dragon.",
      "Assign to Ranch off-duty for milk drop rotation into /guides/medicine crafting.",
    ],
  }),

  palGuide({
    slug: "helzephyr", name: "Helzephyr", role: "Dark Flier", element: "Dark",
    location: "Helzephyr spawns at night in Deep Sand Dunes (280,-350) and Ruined Fortress City (-125,-440). Always Alpha at (-140,-460) around level 43.",
    desc: "Helzephyr guide — Dark night flier. Best breeding combos, saddle DPS.",
    keywords: "helzephyr guide, palworld helzephyr, how to catch helzephyr, helzephyr breeding, helzephyr saddle, helzephyr night, palworld dark flier",
    overview: [
      "Element: Dark. Base stats level 43: HP 3,900 / ATK 140 / DEF 110.",
      "Partner Skill 'Night Wing' halves stamina drain while flying at night — best nocturnal mount.",
      "Pairs with /guides/tier-list night rotations; no official variant form exists.",
    ],
    breed: [
      "Nitewing × Cawgnito → Helzephyr.",
      "Vanwyrm × Nox → Helzephyr.",
      "Helzephyr × Menasting → Helzephyr with Dark passives.",
    ],
    strategy: [
      "Best night traversal mount — pair with /guides/tech-tree unlock for Dark Aura schematic.",
      "Passives: Legend, Ferocious, Nocturnal, Swift.",
      "Combat: strong /guides/bosses counter vs Neutral tower bosses.",
    ],
  }),

  palGuide({
    slug: "vanwyrm", name: "Vanwyrm", role: "Fire/Dark Flier", element: "Fire / Dark",
    location: "Vanwyrm roams Mount Obsidian ridges (245,-395) and the Ruined Fortress City. Cryst variant spawns in Land of Absolute Zero.",
    desc: "Vanwyrm guide — Fire/Dark flier with premier Handiwork stats. Breeding, saddle, Cryst variant and best passives.",
    keywords: "vanwyrm guide, palworld vanwyrm, vanwyrm cryst, how to catch vanwyrm, vanwyrm breeding, vanwyrm saddle, vanwyrm handiwork, palworld fire dark flier",
    overview: [
      "Element: Fire with Dark sub-typing (Cryst is Ice hybrid).",
      "Base stats level 38: HP 3,000 / ATK 125 / DEF 95. Kindling 2 + Transporting 3.",
      "Best Transporting Pal in the game — pair with Anubis for a perfect base logistics loop.",
    ],
    breed: [
      "Nitewing × Blazehowl → Vanwyrm.",
      "Vanwyrm × Foxcicle → Vanwyrm Cryst.",
      "Vanwyrm × Ragnahawk → /pals/ragnahawk pass-through.",
    ],
    strategy: [
      "Assign to Base Transporting queue for auto-haul between Mining Sites and Assembly Line.",
      "Passives: Workaholic, Serious, Artisan, Swift.",
      "Combat: mid-tier flier — retire for /guides/jetragon post-Level 50.",
    ],
  }),

  palGuide({
    slug: "flambelle", name: "Flambelle", role: "Fire Support", element: "Fire",
    location: "Flambelle floats around Mount Obsidian magma pools (270,-410) and Sealed Realm of the Blazing Beast (335,-465). Uses candle drops for /guides/medicine.",
    desc: "Flambelle guide — Fire support Pal that feeds candles into /guides/medicine and refined ingots. Breeding, passives, base setup.",
    keywords: "flambelle guide, palworld flambelle, how to catch flambelle, flambelle ranch, flambelle breeding, flambelle candle, palworld medicine flambelle, flambelle passives",
    overview: [
      "Element: Fire. Base stats level 15: HP 900 / ATK 55 / DEF 55. Kindling 2.",
      "Partner Skill 'Candle Drop' produces Flame Organ passively on the Ranch.",
      "Required for /guides/medicine Tier 3 recipes (candle-based potions).",
    ],
    breed: [
      "Foxparks × Hoocrates → Flambelle.",
      "Flambelle × Rooby → Flambelle passive stack.",
      "Flambelle × Anubis → skip via /guides/incineram fallback.",
    ],
    strategy: [
      "Assign to Ranch first, then rotate to Furnace Mk II for +15% smelt speed at Level 30.",
      "Passives: Workaholic, Serious, Artisan, Lucky.",
      "Not a combat pick — swap for /pals/blazehowl.",
    ],
  }),

  // ---------------- Eidrolon variants ----------------
  

  // ---------------- Ragnahawk variants ----------------
  palGuide({
    slug: "ragnahawk-aqua", name: "Ragnahawk Aqua", role: "Fire/Water Flier", element: "Fire / Water",
    location: "Ragnahawk Aqua is a Palworld 1.0 dual-type variant found in Sea Breeze Archipelago cliffs (260, 305). Alpha spawns at (275, 320) on daily reset.",
    desc: "Ragnahawk Aqua Palworld guide — dual Fire/Water flier. Fastest breeding path, saddle stats and best passives.",
    keywords: "ragnahawk aqua guide, palworld ragnahawk aqua, how to catch ragnahawk aqua, ragnahawk aqua breeding, ragnahawk aqua saddle, palworld fire water flier",
    overview: [
      "Element: Fire / Water — flips resistances mid-fight. See /elements.",
      "Base stats level 40: HP 3,600 / ATK 130 / DEF 95.",
      "Partner Skill 'Rain Dance' extinguishes Fire status and heals Water Pals.",
    ],
    breed: [
      "Ragnahawk × Jormuntide → Ragnahawk Aqua.",
      "Blazehowl × Azurobe → Ragnahawk Aqua fallback.",
      "See /guides/ragnahawk for base combos.",
    ],
    strategy: [
      "Flip element mid-fight vs mixed elemental raids — Bellanoir Libero especially.",
      "Passives: Legend, Ferocious, Divine Dragon, Nitro.",
      "Combat use in /guides/event-syndicate-raid phase 2 for Water damage window.",
    ],
  }),

  palGuide({
    slug: "astral-ragnahawk", name: "Astral Ragnahawk", role: "Astral Fusion Flier", element: "Fire / Neutral",
    location: "Astral Ragnahawk is a Feybreak fusion available at the Astral Altar (390, 145). Requires one Ragnahawk + one Celaray + Ancient Civilization Part.",
    desc: "Astral Ragnahawk guide — Feybreak fusion flier with Astral Beam ult. Fusion recipe, passives and endgame use.",
    keywords: "astral ragnahawk guide, palworld astral ragnahawk, astral ragnahawk fusion, ragnahawk astral, palworld astral fusion, astral ragnahawk passives",
    overview: [
      "Element: Fire / Neutral — 25% bonus damage vs Ghost variants.",
      "Base stats level 45: HP 4,200 / ATK 145 / DEF 100.",
      "Partner Skill 'Astral Beam' fires a 300-power column attack every 30s while mounted.",
    ],
    breed: [
      "Fusion-only — Ragnahawk + Celaray at the Astral Altar.",
      "Passives on both parents inherit — pre-roll Legend + Ferocious.",
      "See /guides/tech-tree for Altar unlock order.",
    ],
    strategy: [
      "Best air-to-ground damage platform under /guides/jetragon speed.",
      "Passives: Legend, Ferocious, Divine Dragon, Fire Emperor.",
      "Use in /guides/palworld-1-0-endgame post-Xenolord clear.",
    ],
  }),

  // ---------------- Blazehowl variants ----------------
  palGuide({
    slug: "blazehowl-noct", name: "Blazehowl Noct", role: "Dark Fire Mount", element: "Fire / Dark",
    location: "Blazehowl Noct spawns at night in Ruined Fortress City (-125,-440) and near Anubis' arena. Level 38 Alpha at (-140,-460).",
    desc: "Blazehowl Noct guide — the Dark-typed Blazehowl variant. Breeding, saddle DPS and best raid comps.",
    keywords: "blazehowl noct guide, palworld blazehowl noct, how to catch blazehowl noct, blazehowl noct breeding, blazehowl noct saddle, palworld dark fire mount",
    overview: [
      "Element: Fire / Dark. Base stats level 38: HP 3,300 / ATK 135 / DEF 100.",
      "Partner Skill 'Nightflame' halves stamina drain at night and grants +20% Dark damage.",
      "Best Dark-mount alternative to /pals/necromus for players without Legendaries.",
    ],
    breed: [
      "Blazehowl × Nox → Blazehowl Noct.",
      "Incineram Noct × Blazehowl → Blazehowl Noct fallback.",
      "See /guides/blazehowl for base combos.",
    ],
    strategy: [
      "Best mid-game Dark mount — bridges the gap to /pals/necromus.",
      "Passives: Legend, Ferocious, Nocturnal, Musclehead.",
      "Combat: pair with /pals/anubis in /guides/event-tower-boss-battle.",
    ],
  }),

  
  // ---------------- Lyleen, Mossanda, Noct variants ----------------
  palGuide({
    slug: "lyleen-noct", name: "Lyleen Noct", role: "Dark Grass Healer", element: "Grass / Dark",
    location: "Lyleen Noct spawns at night in Bamboo Groves (160,-95) and appears as an Alpha at (180,-105) around level 40.",
    desc: "Lyleen Noct guide — Dark/Grass healer variant. Breeding, saddle heal buff, and best base assignments.",
    keywords: "lyleen noct guide, palworld lyleen noct, how to catch lyleen noct, lyleen noct breeding, lyleen noct saddle, palworld dark grass healer",
    overview: [
      "Element: Grass / Dark. Base stats level 40: HP 4,200 / ATK 140 / DEF 110. Medicine Production 3.",
      "Partner Skill 'Nightbloom' heals the player 3%/sec at night — the strongest sustained heal aura.",
      "Best /guides/medicine crafter in the game.",
    ],
    breed: [
      "Lyleen × Nox → Lyleen Noct.",
      "Petallia × Menasting → Lyleen Noct fallback.",
      "See /guides/lyleen for base combos.",
    ],
    strategy: [
      "Assign to Medicine Bench Mk II — halves recipe time on all Tier 4 potions.",
      "Passives: Motherly Love, Serenity, Nocturnal, Legend.",
      "Combat: keep summoned during /guides/event-syndicate-raid for the sustained heal ring.",
    ],
  }),


  palGuide({
    slug: "mossanda-lux", name: "Mossanda Lux", role: "Grass/Electric Hybrid", element: "Grass / Electric",
    location: "Mossanda Lux is an Electric hybrid found near Fisherman's Point marshlands (215, 155) and Bridge of the Twin Knights during storms.",
    desc: "Mossanda Lux guide — Electric/Grass hybrid mount. Breeding, saddle DPS and best passive setup.",
    keywords: "mossanda lux guide, palworld mossanda lux, how to catch mossanda lux, mossanda lux breeding, mossanda lux saddle, palworld electric grass mount",
    overview: [
      "Element: Grass / Electric. Base stats level 38: HP 4,000 / ATK 140 / DEF 115.",
      "Partner Skill 'Storm Fist' delivers a 500-power melee combo every 25s.",
      "Best Electric-DPS mount for players without /guides/grizzbolt-rex.",
    ],
    breed: [
      "Mossanda × Grizzbolt → Mossanda Lux.",
      "Mossanda × Sparkit → Mossanda Lux fallback.",
      "See /guides/mossanda for base combos.",
    ],
    strategy: [
      "Mount for lightning storms — grants +30% Electric damage during weather events.",
      "Passives: Legend, Lord of Lightning, Ferocious, Musclehead.",
      "Assign to Electric Generator Mk II when not mounted.",
    ],
  }),

  // ---------------- Necromus, Grizzbolt, Petallia, Lamball, Pengullet variants ----------------
  fusion("Jetragon Astral", "jetragon-astral", "Jetragon", "Dragon / Neutral", "Astral Beam replaces the missile — hits ghost variants.", [
    "Best counter vs high-mobility Dark fliers in late-game raids.",
    "See /guides/astral-ragnahawk for the parallel Ragnahawk path.",
  ]),
  fusion("Jetragon Ignis", "jetragon-ignis", "Jetragon", "Dragon / Fire", "Fire missile launcher variant — +25% damage vs Grass raids.", [
    "Best mount for Bellanoir Libero clears.",
    "Recipe: Jetragon + Ragnahawk at Feybreak Altar.",
  ]),
  fusion("Shadowbeak Lord", "shadowbeak-lord", "Shadowbeak", "Dark", "Ascended Dark flier — grants +30% Dark damage aura to teammates.", [
    "Requires 3× Shadowbeak with Legend passive.",
    "Best team-buff mount for Dark comps.",
  ]),
  fusion("Grizzbolt Rex", "grizzbolt-rex", "Grizzbolt", "Electric / Dragon", "Rex form adds Dragon damage type — bypasses Electric resists.", [
    "Best DPS in /guides/event-syndicate-raid phase 2 for players without /pals/jetragon.",
    "Recipe: Grizzbolt + Chillet at Feybreak Altar.",
  ]),
  fusion("Petallia Terra", "petallia-terra", "Petallia", "Grass / Ground", "Terra form roots enemies in 15m radius every 20s — top-tier crowd control.", [
    "Best support in /guides/event-tower-boss-battle vs mobile bosses.",
    "Recipe: Petallia + Digtoise at Feybreak Altar.",
  ]),
  fusion("Lamball Cryst", "lamball-cryst", "Lamball", "Neutral / Ice", "Cryst variant grants +20 cold resistance while in party.", [
    "Best beginner Ice hybrid — no boss required.",
    "Recipe: Lamball + Pengullet at Small Altar.",
  ]),
  fusion("Depresso Noct", "depresso-noct", "Depresso", "Dark", "Dark-only Depresso variant with Nocturnal passive baked in.", [
    "Assign to Base Handiwork bench at night for +25% craft speed.",
    "Recipe: Depresso + Nox at Basic Altar.",
  ]),
  fusion("Ribbuny Cryst", "ribbuny-cryst", "Ribbuny", "Neutral / Ice", "Cryst variant grants party-wide cold resistance +25.", [
    "Cheapest Ice-resist companion for /guides/event-tower-boss-battle cold zones.",
    "Recipe: Ribbuny + Pengullet at Small Altar.",
  ]),
  fusion("Pengullet Frost", "pengullet-frost", "Pengullet", "Water / Ice", "Frost variant freezes water tiles on contact for base plumbing.", [
    "Great decorative + utility Pal — see /guides/ore-coal-farming-bases.",
    "Recipe: Pengullet + Foxcicle at Small Altar.",
  ]),
  fusion("Vanwyrm Cryst", "vanwyrm-cryst", "Vanwyrm", "Ice", "Cryst form drops Dark, adds pure Ice damage.", [
    "Best Ice mount before /pals/frostallion.",
    "Recipe: Vanwyrm + Foxcicle at Small Altar.",
  ]),
  fusion("Jormuntide Ignis", "jormuntide-ignis", "Jormuntide", "Fire / Dragon", "Fire variant swaps Water for Fire — best Fire dragon mount.", [
    "Direct catch also possible at Volcano ridge (325,-380).",
    "See /guides/jormuntide for base variant.",
  ]),

  // ---------------- Lucky variants ----------------
  lucky("Anubis", "anubis", "Ground", "Lamball × Menasting → Anubis (2% Lucky roll on hatch).", "Lucky Anubis is one of the rarest shinies — spawn ring is the Anubis arena (144, -140)."),
  lucky("Cattiva", "cattiva", "Neutral", "Lamball × Chikipi → Cattiva (1% Lucky hatch roll).", "Lucky Cattiva is a Windswept Hills camp target — easiest first Lucky."),
  lucky("Foxparks", "foxparks", "Fire", "Foxparks × Foxparks → Lucky Foxparks (1% Lucky pass).", "Lucky Foxparks doubles Kindling output — top Ranch buff."),
  lucky("Frostallion", "frostallion", "Ice", "Frostallion × Frostallion → Lucky Frostallion (guaranteed on 1.0 Palworld shiny farm).", "Lucky Frostallion grants party-wide +25 cold resistance while summoned."),
  lucky("Jetragon", "jetragon", "Dragon", "Jetragon × Jetragon → Lucky Jetragon (1% Lucky pass).", "Lucky Jetragon is the fastest mount in the game (+5% speed on base Jetragon)."),
  lucky("Lamball", "lamball", "Neutral", "Lamball × Lamball → Lucky Lamball (1% Lucky pass).", "Lucky Lamball is the easiest first shiny in the game — always start here for the shiny-hunter achievement."),
  lucky("Shadowbeak", "shadowbeak", "Dark", "Shadowbeak × Shadowbeak → Lucky Shadowbeak (1% Lucky pass).", "Lucky Shadowbeak is a Dark-team centerpiece — pair with /pals/necromus."),
];
